package com.example.mystybag.appCore.views

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.runtime.Composable
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.key
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import dev.icerock.moko.resources.compose.stringResource
import com.example.mystybag.appCore.logic.MystyBagDigitalWalletViewModel.Companion.PAGE_BAGS
import com.example.mystybag.appCore.logic.MystyBagDigitalWalletViewModel.Companion.PAGE_DELIVERY
import com.example.mystybag.appCore.logic.MystyBagDigitalWalletViewModel.Companion.PAGE_DASHBOARD
import com.example.mystybag.appCore.logic.MystyBagDigitalWalletViewModel.Companion.PAGE_FAVORITES
import com.example.mystybag.appCore.logic.MystyBagDigitalWalletViewModel.Companion.PAGE_HOME
import com.example.mystybag.appCore.logic.MystyBagDigitalWalletViewModel.Companion.PAGE_LOCATIONS
import com.example.mystybag.appCore.logic.MystyBagDigitalWalletViewModel.Companion.PAGE_MENU
import com.example.mystybag.appCore.logic.MystyBagDigitalWalletViewModel.Companion.PAGE_PROFILE
import com.example.mystybag.appCore.logic.MainViewModel
import com.example.mystybag.appCore.logic.pages.HomePageViewModel
import com.example.mystybag.appCore.model.pages.HomeViewMode
import com.example.mystybag.appCore.views.components.ClientWebHeaderBar
import com.example.mystybag.appCore.views.components.WebHeaderBottomDivider
import com.example.mystybag.appCore.views.pages.ActiveFilterChipsRow
import com.example.mystybag.common.expected.hideWebHomeMapOverlayNow
import com.example.mystybag.common.expected.showWebHomeMapOverlayNow
import com.example.mystybag.common.resources.SR
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_JWT
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.core.helpers.WebLocaleComposition

/** За DOM картата на web: [HomePageUIState.viewMode] през [subscribeAsState] (не еднократно [.value]). */
@Composable
internal fun subscribeHomeViewModeForWebMap(homePageViewModel: HomePageViewModel?): HomeViewMode? {
    if (homePageViewModel == null) return null
    val homeState = homePageViewModel.uiState.subscribeAsState()
    return homeState.value.viewMode
}

/**
 * Web **landscape** shell: [ClientWebHeaderBar], разделител, съдържание (pager).
 * [MainView] държи само нативно мобилно и портретен уеб — един и същ интерфейс (pager + долна лента).
 */
@Composable
internal fun MainWebView(
    viewModel: MainViewModel,
    pagerContent: @Composable () -> Unit,
) {
    val pages = viewModel.pages.subscribeAsState()
    val merchantJwt = Preferences.getString(key = PREFERENCES_MERCHANT_JWT, "")
    val isMerchant = merchantJwt.isNotEmpty()
    val selectedIndex = pages.value.selectedIndex
    val homePageViewModel = pages.value.items.firstOrNull {
        it.instance is HomePageViewModel
    }?.instance as? HomePageViewModel
    val homeViewMode = subscribeHomeViewModeForWebMap(homePageViewModel)
    val webLocaleKey = WebLocaleComposition.revision

    key(webLocaleKey) {
        Column(Modifier.fillMaxSize()) {
            if (isMerchant) {
                ClientWebHeaderBar(
                    selectedTab = selectedIndex,
                    homeTabIndex = PAGE_DASHBOARD,
                    deliveryTabIndex = PAGE_BAGS,
                    favoritesTabIndex = PAGE_LOCATIONS,
                    menuTabIndex = PAGE_MENU,
                    searchCallbacks = null,
                    onFilterClick = {},
                    showSearchAndFilter = false,
                    onHomeClick = { viewModel.selectPage(PAGE_DASHBOARD, fromView = true) },
                    onOrdersClick = { viewModel.selectPage(PAGE_BAGS, fromView = true) },
                    onFavoritesClick = { viewModel.selectPage(PAGE_LOCATIONS, fromView = true) },
                    onMenuClick = { viewModel.selectPage(PAGE_MENU, fromView = true) },
                    homeLabel = stringResource(SR.strings.overview),
                    deliveryLabel = stringResource(SR.strings.bags),
                    favoritesLabel = stringResource(SR.strings.objects),
                    menuLabel = stringResource(SR.strings.menu),
                )
            } else {
                ClientWebHeaderBar(
                    selectedTab = selectedIndex,
                    homeTabIndex = PAGE_HOME,
                    deliveryTabIndex = PAGE_DELIVERY,
                    favoritesTabIndex = PAGE_FAVORITES,
                    menuTabIndex = PAGE_PROFILE,
                    searchCallbacks = homePageViewModel?.searchControls,
                    onFilterClick = {
                        if (homePageViewModel != null) {
                            if (selectedIndex != PAGE_HOME) {
                                viewModel.selectPage(PAGE_HOME, fromView = true)
                            }
                            homePageViewModel.onFilterClick()
                        }
                    },
                    onHomeClick = { viewModel.selectPage(PAGE_HOME, fromView = true) },
                    onOrdersClick = { viewModel.selectPage(PAGE_DELIVERY, fromView = true) },
                    onFavoritesClick = { viewModel.selectPage(PAGE_FAVORITES, fromView = true) },
                    onMenuClick = { viewModel.selectPage(PAGE_PROFILE, fromView = true) },
                    contentBelowSearchRow =
                        if (selectedIndex == PAGE_HOME && homePageViewModel != null) {
                            val homeVm = homePageViewModel
                            @Composable {
                                val homeState = homeVm.uiState.subscribeAsState()
                                val homeUiState = homeState.value
                                val activeTypes =
                                    homeUiState.appliedFilter?.activeFilterTypes().orEmpty()
                                if (activeTypes.isNotEmpty()) {
                                    Column(modifier = Modifier.padding(top = 8.dp)) {
                                        ActiveFilterChipsRow(
                                            filterTypes = activeTypes,
                                            onClearChip = { type ->
                                                homeVm.clearFilterChip(type)
                                            }
                                        )
                                    }
                                }
                            }
                        } else {
                            null
                        },
                )
            }
            WebHeaderBottomDivider()
            if (isMerchant) {
                Box(
                    modifier = Modifier.fillMaxWidth().weight(1f),
                    contentAlignment = Alignment.TopCenter
                ) {
                    Box(
                        modifier = Modifier
                            .width(1080.dp)
                            .fillMaxHeight()
                    ) {
                        pagerContent()
                    }
                }
            } else {
                Box(Modifier.fillMaxWidth().weight(1f)) {
                    pagerContent()
                }
            }
        }
    }

    SideEffect {
        val shouldShowHomeMap = selectedIndex == PAGE_HOME && homeViewMode == HomeViewMode.MAP
        if (shouldShowHomeMap) {
            showWebHomeMapOverlayNow()
        } else {
            hideWebHomeMapOverlayNow()
        }
    }
}
